import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IUserTranslatedString } from "@faire/web-api/indigofair/data/IUserTranslatedString";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import { ILocaleKey } from "@faire/web-api/indigofair/locale/ILocaleKey";
import { sortOptionsAndVariations } from "@faire/web/common/products/sortOptionsAndVariations";
import { protoValues } from "@faire/web/common/typescriptUtils";
import isUndefined from "lodash/isUndefined";

export const getProductName = (
  product?: IProductPageResponse.IPdpProduct
): string | undefined => {
  return product?.name?.translated ?? product?.name?.original;
};

export const getProductDescription = (
  product?: IProductPageResponse.IPdpProduct
): string | undefined => {
  return (
    product?.details?.description?.translated ??
    product?.details?.description?.original
  );
};

export const getProductOptions = (
  product?: IProductPageResponse.IPdpProduct
): IProductOption[] => {
  if (isUndefined(product) || isUndefined(product.options)) {
    return [];
  }
  const options = protoValues(product.options) as IProductOption[];
  return options;
};

export const getSortedProductOptions = (
  product?: IProductPageResponse.IPdpProduct
): IProductOption[] => {
  if (isUndefined(product) || isUndefined(product.options)) {
    return [];
  }
  const productDetails = getProductDetails(product);
  sortOptionsAndVariations(productDetails);
  return productDetails.options;
};

export const getProductOptionMap = (
  product: IProduct
): Partial<Record<string, IProductOption>> => {
  return product.options.reduce((acc, option) => {
    if (option.token) {
      acc[option.token] = IProductOption.build(option);
    }
    return acc;
  }, {} as Partial<Record<string, IProductOption>>);
};

export const getProductDetails = (
  product: IProductPageResponse.IPdpProduct,
  brandToken?: string
): IProduct => {
  return IProduct.build({
    brand_token: brandToken,
    description: getProductDescription(product),
    options: getProductOptions(product),
    images: product.visuals?.images as IImage[],
    allow_sales_when_out_of_stock:
      product.purchase_information?.allow_sales_when_out_of_stock,
    unit_multiplier: product.purchase_information?.unit_multiplier,
    per_style_min_order_quantity:
      product.purchase_information?.per_style_min_order_quantity,
    non_returnable_reason:
      product.purchase_information?.non_returnable_reason_data
        ?.non_returnable_reason,
    name: getProductName(product),
    token: product.token,
    board_tokens: product.board_tokens,
    is_high_sell_through: product.purchase_information?.is_high_sell_through,
    state: product.state,
    customizations: product.purchase_information?.customizations,
    max_option_wholesale_price:
      product.purchase_information?.max_option_wholesale_price,
    max_option_retail_price:
      product.purchase_information?.max_option_retail_price,
    preorderable: product.purchase_information?.is_preorderable,
    is_highlighted_product_for_promotional_event:
      product.is_highlighted_product_for_promotional_event,
    variation_map: product.purchase_information?.variation_map,
  });
};

export const getPdpProductFromProduct = (
  product: IProduct
): IProductPageResponse.IPdpProduct => {
  return IProductPageResponse.IPdpProduct.build({
    token: product.token,
    name: IUserTranslatedString.build({
      original: product.name,
    }),
    options: getProductOptionMap(product),
    state: product.state,
    board_tokens: product.board_tokens,
    is_promoted: product.is_promoted,
    promoted_by: product.promoted_by,
    is_highlighted_product_for_promotional_event:
      product.is_highlighted_product_for_promotional_event,
    visuals: IProductPageResponse.IPdpProduct.IVisuals.build({
      images: product.images,
    }),
    purchase_information:
      IProductPageResponse.IPdpProduct.IPurchaseInformation.build({
        order_by_date: product.order_by_date,
        unit_multiplier: product.unit_multiplier,
        tester_price: product.tester_price,
        tester_promo_price: product.tester_promo_price,
        tester_prices: product.tester_prices,
        min_order_quantity: product.min_order_quantity,
        per_style_min_order_quantity: product.per_style_min_order_quantity,
        product_orderability_counts: product.product_orderability_counts,
        non_returnable_reason_data: product.non_returnable_reason_data,
        style_groups: product.style_groups,
        variation_map: product.variation_map,
        customizations: product.customizations,
        is_high_sell_through: product.is_high_sell_through,
        is_new: product.is_new,
        allow_sales_when_out_of_stock: product.allow_sales_when_out_of_stock,
        max_option_retail_price: product.max_option_retail_price,
        max_option_wholesale_price: product.max_option_wholesale_price,
        has_pre_packs_available: product.has_pre_packs_available,
        is_preorderable: product.preorderable,
      }),
    details: IProductPageResponse.IPdpProduct.IDetails.build({
      taxonomy_type: product.taxonomy_type,
      description: IUserTranslatedString.build({
        translated: product.description,
      }),
      information_sections: product.product_information_sections,
      made_in: IUserTranslatedString.build({
        original: product.made_in_country,
        original_locale_key: ILocaleKey.build({
          country: product.made_in_country as keyof typeof ISO3166Alpha3,
        }),
      }),
    }),
    shipping_information:
      IProductPageResponse.IPdpProduct.IShippingInformation.build({
        expected_ship_date: product.expected_ship_date,
        expected_ship_window_days: product.expected_ship_window_days,
        has_mixed_ship_dates: product.has_mixed_ship_dates,
      }),
  });
};

export function isMixedProduct(
  product: IProductPageResponse.IPdpProduct
): boolean {
  const productOptions = getProductOptions(product);
  if (!productOptions.length) {
    return false;
  }
  const type = productOptions[0]?.orderability_type;
  const startDate = productOptions[0]?.expected_ship_date;
  const shipWindowDays = productOptions[0]?.expected_ship_window_days;
  const stopSellingAt = productOptions[0]?.stop_selling_at;
  return productOptions.some(
    (option) =>
      option.orderability_type !== type ||
      option.expected_ship_date !== startDate ||
      option.expected_ship_window_days !== shipWindowDays ||
      option.stop_selling_at !== stopSellingAt
  );
}

export const getShowroomProductData = (
  product: IProduct
): IProductPageResponse.IPdpProduct => {
  const pdpProduct = getPdpProductFromProduct(product);
  return IProductPageResponse.IPdpProduct.build({
    ...pdpProduct,
    purchase_information:
      IProductPageResponse.IPdpProduct.IPurchaseInformation.build({
        ...pdpProduct.purchase_information,
        is_mid_price_point_brand_apparel_product: true,
      }),
    visuals: IProductPageResponse.IPdpProduct.IVisuals.build({
      ...pdpProduct.visuals,
      show_vertical_image: true,
    }),
  });
};
